/**
 * Apply. Form A, one section per step.
 *
 * Every field on this screen is rendered from `FORM_A_SECTIONS`, which was
 * transcribed from the Gazette notification that substituted Form A in 2021.
 * The clause letter sits next to each label so an applicant holding the printed
 * form can follow along, and the whole draft is written to the browser after
 * every keystroke so a refresh costs nothing.
 */

import { useEffect, useMemo, useState } from "react";
import {
  bi,
  FORM_A_PROVENANCE,
  FORM_A_SECTIONS,
  statute,
  validateFormA,
  type Bilingual,
  type FormASection,
} from "@ksidc/ontology";
import { ApiError, useLang, useSession } from "@ksidc/client";
import type {
  AreasResponse,
  ClearanceCatalogueResponse,
  CatalogueClearance,
  FormAErrorBody,
  NoBoardBody,
  SubmitResponse,
} from "../api-types.js";
import { BOARD_NAMES, days } from "../format.js";
import { clearDraft, loadDraft, saveDraft } from "../draft.js";
import { FillFromDocuments } from "../components/FillFromDocuments.js";
import { navigate, statusHref } from "../router.js";
import type { AreaOption } from "../components/FieldRenderer.js";
import { FieldRenderer } from "../components/FieldRenderer.js";
import { Callout, EmptyState, ErrorNote, Loading, Panel, ProvenanceRow } from "../components/ui.js";

const CLEARANCES_SECTION_KEY = "clearances";
const CLEARANCES_FIELD_KEY = "clearances.requested";

/** Section XIII is stored as the table the Gazette prescribes. */
function buildPayload(
  formA: Record<string, unknown>,
  clearanceTypeIds: string[],
): Record<string, unknown> {
  return {
    ...formA,
    [CLEARANCES_FIELD_KEY]: clearanceTypeIds.map((id) => ({
      clearanceTypeId: id,
      purpose: "",
    })),
  };
}

/**
 * A drafted answer arrives as text, whatever the field is. It is stored in the
 * type the form expects so what the applicant sees on screen is exactly what
 * the payload will carry, and so a wrong reading shows itself rather than
 * hiding behind a cast at submission.
 */
function coerceDrafted(values: Record<string, string>): Record<string, unknown> {
  const byKey = new Map(
    FORM_A_SECTIONS.flatMap((sec) => sec.fields.map((f) => [f.key, f] as const)),
  );
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(values)) {
    const field = byKey.get(key);
    const text = String(raw).trim();
    if (!field || text === "") continue;
    if (field.type === "number" || field.type === "money_lakh") {
      const n = Number(text.replace(/[^0-9.\-]/g, ""));
      if (Number.isFinite(n)) out[key] = n;
      continue;
    }
    if (field.type === "boolean") {
      const yes = /^(true|yes|y|1)$/i.test(text);
      const no = /^(false|no|n|0)$/i.test(text);
      if (yes || no) out[key] = yes;
      continue;
    }
    if (field.type === "select" && field.options?.length) {
      const hit = field.options.find(
        (o) => o.en.toLowerCase() === text.toLowerCase() || o.ml === text,
      );
      out[key] = hit ? hit.en : text;
      continue;
    }
    out[key] = text;
  }
  return out;
}

function sectionOf(key: string): number {
  return FORM_A_SECTIONS.findIndex((s) => s.fields.some((f) => f.key === key));
}

export function Apply(): JSX.Element {
  const { api } = useSession();
  const { t } = useLang();

  const initial = useMemo(() => loadDraft(), []);
  const [formA, setFormA] = useState<Record<string, unknown>>(initial.formA);
  const [clearanceTypeIds, setClearanceTypeIds] = useState<string[]>(initial.clearanceTypeIds);
  const [step, setStep] = useState(initial.step);
  const [catalogue, setCatalogue] = useState<CatalogueClearance[] | null>(null);
  const [areas, setAreas] = useState<AreaOption[]>([]);
  const [serverErrors, setServerErrors] = useState<Record<string, Bilingual>>({});
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<{ title: Bilingual; body: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<SubmitResponse | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [c, a] = await Promise.all([
          api.get<ClearanceCatalogueResponse>("/reference/clearances?pilot=1"),
          api.get<AreasResponse>("/reference/areas"),
        ]);
        if (!live) return;
        setCatalogue(c.clearances);
        setAreas(
          a.areas.map((x) => ({
            id: x.id,
            label: x.name,
            boardConstituted: x.boardConstituted,
          })),
        );
      } catch {
        if (live) setCatalogue([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [api]);

  useEffect(() => {
    saveDraft({ formA, clearanceTypeIds, step, savedAt: "" });
  }, [formA, clearanceTypeIds, step]);

  const totalSteps = FORM_A_SECTIONS.length + 1;
  const onReview = step >= FORM_A_SECTIONS.length;
  const section: FormASection | undefined = FORM_A_SECTIONS[step];

  const payload = useMemo(() => buildPayload(formA, clearanceTypeIds), [formA, clearanceTypeIds]);
  const clientErrors = useMemo(() => validateFormA(payload), [payload]);
  const clientErrorByKey = useMemo(() => {
    const map: Record<string, Bilingual> = {};
    for (const e of clientErrors) map[e.key] = e.message;
    return map;
  }, [clientErrors]);

  const setField = (key: string, value: unknown): void => {
    setFormA((prev) => ({ ...prev, [key]: value }));
    setServerErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const go = (next: number): void => {
    setStep(Math.max(0, Math.min(totalSteps - 1, next)));
    window.scrollTo({ top: 0 });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    setFailure(null);
    try {
      const res = await api.post<SubmitResponse>("/applications", {
        formA: payload,
        clearanceTypeIds,
      });
      clearDraft();
      setDone(res);
      window.scrollTo({ top: 0 });
    } catch (e) {
      if (e instanceof ApiError && e.status === 400 && e.code === "invalid_form_a") {
        const body = e.body as FormAErrorBody;
        const map: Record<string, Bilingual> = {};
        for (const err of body.errors ?? []) map[err.key] = err.message;
        setServerErrors(map);
        const first = Object.keys(map)[0];
        const target = first ? sectionOf(first) : -1;
        if (target >= 0) go(target);
        else go(totalSteps - 1);
      } else if (e instanceof ApiError && e.status === 409 && e.code === "no_board") {
        const body = e.body as NoBoardBody;
        setFailure({
          title: bi(
            "This application cannot be registered yet",
            "ഈ അപേക്ഷ ഇപ്പോൾ രജിസ്റ്റർ ചെയ്യാൻ കഴിയില്ല",
          ),
          body: body.message,
        });
      } else {
        setError(e);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <Submitted result={done} />;

  const errorFor = (key: string): Bilingual | null => serverErrors[key] ?? null;

  return (
    <div className="stack">
      <div>
        <h1>{t(bi("Apply", "അപേക്ഷിക്കുക"))}</h1>
        <p className="lede">
          {t(
            bi(
              "One form covers every clearance. Your answers are saved on this device as you type, so you can stop and come back later.",
              "എല്ലാ അനുമതികൾക്കും ഒറ്റ ഫോറം. നിങ്ങൾ ടൈപ്പ് ചെയ്യുമ്പോൾ തന്നെ ഇത് ഈ ഉപകരണത്തിൽ സൂക്ഷിക്കുന്നു; നിർത്തി പിന്നീട് തുടരാം.",
            ),
          )}
        </p>
        <ProvenanceRow items={[FORM_A_PROVENANCE]} />
      </div>

      <div>
        <div className="stepbar">
          <span>
            {t(
              bi(
                `Step ${step + 1} of ${totalSteps}`,
                `${totalSteps}-ൽ ${step + 1}-ാം ഘട്ടം`,
              ),
            )}
          </span>
          <span>
            {t(bi("Saved on this device", "ഈ ഉപകരണത്തിൽ സൂക്ഷിച്ചു"))}
          </span>
        </div>
        <div className="progress" role="presentation">
          <span style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
        </div>
      </div>

      {step === 0 && !onReview ? (
        <FillFromDocuments
          onDraft={(values) => {
            // Merge, never replace: anything already typed stands.
            const drafted = coerceDrafted(values);
            setFormA((prev) => ({ ...drafted, ...prev }));
          }}
        />
      ) : null}

      {failure ? (
        <Callout tone="alarm" title={failure.title}>
          <p style={{ marginBottom: 0 }}>{failure.body}</p>
        </Callout>
      ) : null}
      {error ? <ErrorNote error={error} /> : null}

      {onReview ? (
        <Review
          clientErrors={clientErrors}
          serverErrors={serverErrors}
          clearanceTypeIds={clearanceTypeIds}
          onJump={go}
        />
      ) : section ? (
        <Panel>
          <p className="eyebrow">
            {t(bi(`Section ${section.numeral}`, `വിഭാഗം ${section.numeral}`))}
          </p>
          <h2>{t(section.title)}</h2>

          {section.key === CLEARANCES_SECTION_KEY ? (
            <ClearancePicker
              catalogue={catalogue}
              selected={clearanceTypeIds}
              onChange={setClearanceTypeIds}
              error={errorFor(CLEARANCES_FIELD_KEY) ?? clientErrorByKey[CLEARANCES_FIELD_KEY] ?? null}
            />
          ) : (
            section.fields.map((field) => {
              // The area only matters where the applicant said the site is in one.
              if (
                field.key === "land.industrialAreaId" &&
                formA["land.inIndustrialArea"] !== true
              ) {
                return null;
              }
              return (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={formA[field.key]}
                  onChange={(v) => setField(field.key, v)}
                  error={errorFor(field.key)}
                  areaOptions={areas}
                />
              );
            })
          )}
        </Panel>
      ) : null}

      <div className="btn-row no-print">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => go(step - 1)}
          disabled={step === 0}
        >
          {t(bi("Back", "പുറകോട്ട്"))}
        </button>
        {onReview ? (
          <button type="button" className="btn" onClick={() => void submit()} disabled={submitting}>
            {submitting ? <span className="spinner" aria-hidden="true" /> : null}
            {t(bi("Send my application", "എന്റെ അപേക്ഷ അയയ്ക്കുക"))}
          </button>
        ) : (
          <button type="button" className="btn" onClick={() => go(step + 1)}>
            {t(bi("Next", "അടുത്തത്"))}
          </button>
        )}
      </div>

      <p className="muted no-print">
        <button
          type="button"
          className="btn-quiet"
          onClick={() => {
            clearDraft();
            setFormA({});
            setClearanceTypeIds([]);
            setServerErrors({});
            go(0);
          }}
        >
          {t(bi("Clear this draft and start again", "ഈ കരട് മായ്ച്ച് വീണ്ടും തുടങ്ങുക"))}
        </button>
      </p>
    </div>
  );
}

function ClearancePicker({
  catalogue,
  selected,
  onChange,
  error,
}: {
  catalogue: CatalogueClearance[] | null;
  selected: string[];
  onChange: (ids: string[]) => void;
  error: Bilingual | null;
}): JSX.Element {
  const { t } = useLang();

  if (catalogue === null) {
    return <Loading what={bi("Loading the list of clearances", "അനുമതികളുടെ പട്ടിക വായിക്കുന്നു")} />;
  }

  if (catalogue.length === 0) {
    return (
      <EmptyState
        title={bi(
          "The list of clearances has not been loaded yet",
          "അനുമതികളുടെ പട്ടിക ഇതുവരെ ചേർത്തിട്ടില്ല",
        )}
        body={bi(
          "This is the list the Board uses to decide which offices to send your application to. It has not been loaded into this copy of the system yet, so there is nothing to choose from. You can still send your application and the Board will agree the list with you.",
          "ബോർഡ് അധികാരികൾക്ക് അയയ്ക്കുന്ന പട്ടികയാണിത്. ഈ പകർപ്പിൽ അത് ചേർത്തിട്ടില്ല, അതിനാൽ ഇപ്പോൾ തിരഞ്ഞെടുക്കാൻ ഒന്നുമില്ല. നിങ്ങൾക്ക് അപേക്ഷ അയയ്ക്കാം; ബോർഡ് നിങ്ങളുമായി ചേർന്ന് പട്ടിക തീരുമാനിക്കും.",
        )}
        provenance={statute("r.5, Form A section XIII")}
      />
    );
  }

  const groups = new Map<string, CatalogueClearance[]>();
  for (const c of catalogue) {
    const key = c.authority.en;
    const list = groups.get(key);
    if (list) list.push(c);
    else groups.set(key, [c]);
  }

  const toggle = (id: string): void => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <div>
      <p className="field-help">
        {t(
          bi(
            "Choose every clearance you need. The Board sends your application to the offices that give these.",
            "നിങ്ങൾക്ക് ആവശ്യമായ എല്ലാ അനുമതികളും തിരഞ്ഞെടുക്കുക. ഈ പട്ടികയാണ് ബോർഡ് അധികാരികൾക്ക് അയയ്ക്കുന്നത്.",
          ),
        )}
      </p>

      {error ? (
        <p className="field-error" role="alert">
          {t(error)}
        </p>
      ) : null}

      {[...groups.entries()].map(([authority, items]) => (
        <fieldset key={authority} style={{ marginBottom: "1.2rem" }}>
          <legend className="eyebrow">{t(items[0]?.authority ?? bi(authority, authority))}</legend>
          <div className="stack-tight">
            {items.map((c) => (
              <label className="checkbox-row" key={c.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                <span>
                  {t(c.name)}
                  {c.publishedTimelineDays !== null ? (
                    <span className="muted">
                      {" "}
                      ·{" "}
                      {t(
                        bi(
                          `published timeline ${days(c.publishedTimelineDays).en}`,
                          `പ്രസിദ്ധീകരിച്ച സമയപരിധി ${days(c.publishedTimelineDays).ml}`,
                        ),
                      )}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <p className="muted">
        {t(
          bi(
            `${selected.length} chosen.`,
            `${selected.length} എണ്ണം തിരഞ്ഞെടുത്തു.`,
          ),
        )}
      </p>
    </div>
  );
}

function Review({
  clientErrors,
  serverErrors,
  clearanceTypeIds,
  onJump,
}: {
  clientErrors: { key: string; message: Bilingual }[];
  serverErrors: Record<string, Bilingual>;
  clearanceTypeIds: string[];
  onJump: (step: number) => void;
}): JSX.Element {
  const { t } = useLang();
  const outstanding = clientErrors.filter((e) => !(e.key in serverErrors));
  const all = [
    ...outstanding,
    ...Object.entries(serverErrors).map(([key, message]) => ({ key, message })),
  ];

  return (
    <Panel title={bi("Check and send", "പരിശോധിച്ച് അയയ്ക്കുക")}>
      {all.length === 0 ? (
        <>
          <p>
            {t(
              bi(
                "You have answered every question the Gazette marks as required. When you send this, the Board writes it into its register, Form B, straight away. The Board's time limit starts on the day it receives your application.",
                "ഗസറ്റ് നിർബന്ധമാക്കിയ എല്ലാറ്റിനും ഉത്തരം നൽകിയിട്ടുണ്ട്. നിങ്ങൾ ഇത് അയയ്ക്കുമ്പോൾ ബോർഡ് ഉടൻ തന്നെ ഫോറം ബി-യിൽ രജിസ്റ്റർ ചെയ്യും; ലഭിക്കുന്ന ദിവസം മുതൽ നിയമപരമായ കാലാവധി ആരംഭിക്കും.",
              ),
            )}
          </p>
          <ProvenanceRow items={[statute("r.7(1)"), statute("s.10")]} />
          <p className="muted" style={{ marginTop: "1rem" }}>
            {t(
              bi(
                `${clearanceTypeIds.length} clearance(s) chosen in section XIII.`,
                `വിഭാഗം XIII-ൽ ${clearanceTypeIds.length} അനുമതികൾ തിരഞ്ഞെടുത്തു.`,
              ),
            )}
          </p>
        </>
      ) : (
        <>
          <Callout
            tone="warn"
            title={bi(
              "Some answers are still missing",
              "ചില ഉത്തരങ്ങൾ ഇനിയും നൽകാനുണ്ട്",
            )}
          >
            <p style={{ marginBottom: 0 }}>
              {t(
                bi(
                  "These questions are marked as required in the Gazette and still need an answer. Tap one to go to it.",
                  "ഗസറ്റ് നിർബന്ധമാക്കിയ ഉത്തരങ്ങളാണിവ. ഒന്നിൽ തൊട്ടാൽ അതിലേക്ക് പോകാം.",
                ),
              )}
            </p>
          </Callout>
          <ul className="items" style={{ marginTop: "1rem" }}>
            {all.map((e) => {
              const target = sectionOf(e.key);
              const sec = target >= 0 ? FORM_A_SECTIONS[target] : undefined;
              return (
                <li className="item" key={e.key}>
                  <button
                    type="button"
                    className="btn-quiet"
                    onClick={() => onJump(target >= 0 ? target : 0)}
                  >
                    {t(e.message)}
                  </button>
                  {sec ? (
                    <div className="item-sub">
                      {t(
                        bi(
                          `Section ${sec.numeral}, ${sec.title.en}`,
                          `വിഭാഗം ${sec.numeral}, ${sec.title.ml}`,
                        ),
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Panel>
  );
}

function Submitted({ result }: { result: SubmitResponse }): JSX.Element {
  const { t } = useLang();
  return (
    <div className="stack">
      <div>
        <h1>{t(bi("Your application is registered", "നിങ്ങളുടെ അപേക്ഷ രജിസ്റ്റർ ചെയ്തു"))}</h1>
        <p className="lede">
          {t(
            bi(
              "The Board wrote it into its register as soon as it arrived, as the Rules require. Keep the number below. It is your entry in that register.",
              "ചട്ടങ്ങൾ ആവശ്യപ്പെടുന്നതുപോലെ, ലഭിച്ച ഉടൻ തന്നെ ഇത് രജിസ്റ്റർ ചെയ്തു. താഴെയുള്ള നമ്പർ സൂക്ഷിക്കുക; ബോർഡിന്റെ രജിസ്റ്ററിലെ എൻട്രിയാണ് അത്.",
            ),
          )}
        </p>
      </div>

      <section className="notice">
        <p className="notice-head">
          {t(bi("Your reference number", "നിങ്ങളുടെ റഫറൻസ് നമ്പർ"))}
        </p>
        <p className="reference" style={{ fontSize: "1.15rem", fontWeight: 600 }}>
          {result.referenceNumber}
        </p>
        <p className="muted" style={{ marginBottom: 0 }}>
          {t(
            bi(
              "This is your number in the register of applications. The Board keeps that register on Form B and writes each application into it as soon as it arrives.",
              "അപേക്ഷകളുടെ രജിസ്റ്ററിലെ ക്രമനമ്പറാണിത്. ബോർഡ് ഫോറം ബി-യിൽ സൂക്ഷിക്കുന്ന ഈ രജിസ്റ്ററിൽ അപേക്ഷ ലഭിച്ച ഉടൻ എഴുതുന്നു.",
            ),
          )}
        </p>
        <ProvenanceRow items={[statute("r.7(1)")]} />
      </section>

      <Panel title={bi("Which Board will decide it", "ഏത് ബോർഡാണ് ഇത് തീരുമാനിക്കുന്നത്")}>
        <h3 style={{ marginTop: 0 }}>{t(BOARD_NAMES[result.routing.boardType])}</h3>
        <p>{t(result.routing.reason)}</p>
        <ProvenanceRow items={[result.routing.provenance]} />
      </Panel>

      <div className="btn-row no-print">
        <a className="btn" href={statusHref(result.applicationId)}>
          {t(bi("See where it is", "ഇത് എവിടെ എത്തി എന്ന് കാണുക"))}
        </a>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(statusHref(result.applicationId))}
        >
          {t(bi("Add my papers", "എന്റെ രേഖകൾ ചേർക്കുക"))}
        </button>
      </div>
    </div>
  );
}
